import { createFeature, createReducer, on } from '@ngrx/store';
import { DiscoverArticleVM } from '../entities/discover-article.entity';
import { DiscoverIndexVM } from '../entities/discover-index.entity';
import { DiscoverInstrumentVM } from '../entities/discover-instrument.entity';
import {
  DiscoverListAndLinksDetailsInstrumentVM,
  DiscoverListAndLinksDetailsVM,
  DiscoverListsAndLinksVM,
} from '../entities/discover-lists-and-links.entity';
import { DiscoverTeaserVM } from '../entities/discover-teaser.entity';
import { DiscoverTopPerformerGroupVM } from '../entities/discover-top-performer-group.entity';
import { MultiOrderStrategy, MultiOrderStrategyDetails } from '../entities/multi-order-strategy.entity';
import { discoverActions, discoverApiActions } from './discover.actions';

export interface DiscoverState {
  overviewLoading: boolean;
  overviewInitiallyLoaded: boolean;
  indices: Record<string, DiscoverIndexVM>;
  todayTeaser: Record<string, DiscoverTeaserVM>;
  listsAndLinks: Record<string, DiscoverListsAndLinksVM>;
  knowHow: Record<string, DiscoverTeaserVM>;
  megatrends: Record<string, DiscoverTeaserVM>;
  topPerformerGroups: Record<string, DiscoverTopPerformerGroupVM>;
  details: DiscoverArticleVM | null;
  detailsQuotesReady: boolean;
  detailsLoading: boolean;
  listDetails: DiscoverListAndLinksDetailsVM | null;
  listDetailsQuotesReady: boolean;
  listDetailsLoading: boolean;
  multiOrderStrategies: MultiOrderStrategy[];
  multiOrderStrategiesLoading: boolean;
  multiOrderStrategy: MultiOrderStrategyDetails;
  multiOrderStrategyAssetsLoaded: boolean;
}

const initialState: DiscoverState = {
  overviewLoading: false,
  overviewInitiallyLoaded: false,
  indices: {},
  todayTeaser: {},
  listsAndLinks: {},
  knowHow: {},
  megatrends: {},
  topPerformerGroups: {},
  details: null,
  detailsQuotesReady: false,
  detailsLoading: false,
  listDetails: null,
  listDetailsQuotesReady: false,
  listDetailsLoading: false,
  multiOrderStrategies: [],
  multiOrderStrategiesLoading: false,
  multiOrderStrategy: null,
  multiOrderStrategyAssetsLoaded: false,
};

export const discoverFeature = createFeature({
  name: 'discover',
  reducer: createReducer(
    initialState,
    on(
      discoverActions.setOverviewLoading,
      (state, { loading }): DiscoverState => ({
        ...state,
        overviewLoading: loading,
      }),
    ),
    on(
      discoverApiActions.loadDiscoverOverviewSuccess,
      (state, { overview }): DiscoverState => ({
        ...state,
        overviewInitiallyLoaded: true,
        overviewLoading: false,
        indices: overview.indices,
        todayTeaser: overview.todayTeaser,
        listsAndLinks: overview.listsAndLinks,
        knowHow: overview.knowHow,
        megatrends: overview.megatrends,
        topPerformerGroups: overview.topPerformerGroups,
      }),
    ),
    on(
      discoverApiActions.loadDiscoverOverviewFailure,
      (state): DiscoverState => ({
        ...state,
        overviewInitiallyLoaded: true,
        overviewLoading: false,
      }),
    ),
    on(
      discoverActions.updateIndicesQuotes,
      (state, { indices }): DiscoverState => ({
        ...state,
        indices: {
          ...state.indices,
          ...indices.reduce<Record<string, DiscoverIndexVM>>((acc, index) => ({ ...acc, [index.isin]: index }), {}),
        },
      }),
    ),
    on(
      discoverActions.loadDiscoverTodayDetails,
      discoverActions.loadDiscoverKnowHowDetails,
      discoverActions.loadDiscoverMegatrendsDetails,
      (state): DiscoverState => ({
        ...state,
        detailsLoading: true,
        detailsQuotesReady: false,
        details: null,
      }),
    ),
    on(
      discoverApiActions.loadDiscoverArticleDetailsSuccess,
      (state, { details }): DiscoverState => ({
        ...state,
        details,
        detailsLoading: false,
      }),
    ),
    on(
      discoverApiActions.loadDiscoverArticleDetailsFailure,
      (state): DiscoverState => ({
        ...state,
        detailsLoading: false,
        details: null,
      }),
    ),
    on(
      discoverActions.loadDiscoverListDetails,
      (state): DiscoverState => ({
        ...state,
        listDetailsLoading: true,
        listDetailsQuotesReady: false,
        listDetails: null,
      }),
    ),
    on(
      discoverActions.clearDiscoverDetails,
      (state): DiscoverState => ({
        ...state,
        details: null,
        listDetails: null,
      }),
    ),
    on(
      discoverApiActions.loadDiscoverListDetailsSuccess,
      (state, { details }): DiscoverState => ({
        ...state,
        listDetails: details,
        listDetailsLoading: false,
      }),
    ),
    on(
      discoverApiActions.loadDiscoverListDetailsFailure,
      (state): DiscoverState => ({
        ...state,
        listDetailsLoading: false,
        listDetails: null,
      }),
    ),
    on(
      discoverActions.updateDetailsInstrumentsQuotes,
      (state, { instruments }): DiscoverState => ({
        ...state,
        detailsQuotesReady: true,
        details: {
          ...state.details,
          instruments: {
            ...state.details?.instruments,
            ...instruments.reduce<Record<string, DiscoverInstrumentVM>>(
              (acc, instrument) => ({ ...acc, [instrument.isin]: instrument }),
              {},
            ),
          },
        },
      }),
    ),
    on(
      discoverActions.updateListInstrumentsQuotes,
      (state, { instruments }): DiscoverState => ({
        ...state,
        listDetailsQuotesReady: true,
        listDetails: {
          ...state.listDetails,
          instruments: {
            ...state.listDetails?.instruments,
            ...instruments.reduce<Record<string, DiscoverListAndLinksDetailsInstrumentVM>>(
              (acc, instrument) => ({
                ...acc,
                [instrument.isin]: {
                  ...state.listDetails?.instruments[instrument.isin],
                  ...instrument,
                },
              }),
              {},
            ),
          },
        },
      }),
    ),
    on(discoverActions.updateTopPerformerInstrumentsQuotes, (state, { instruments }): DiscoverState => {
      const instrumentsLookup = instruments.reduce((acc, instrument) => {
        acc[instrument.isin] = instrument;
        return acc;
      }, {});

      return {
        ...state,
        topPerformerGroups: Object.keys(state.topPerformerGroups).reduce<Record<string, DiscoverTopPerformerGroupVM>>(
          (acc, groupIdentifier) => ({
            ...acc,
            [groupIdentifier]: {
              ...state.topPerformerGroups[groupIdentifier],
              instruments: state.topPerformerGroups[groupIdentifier].instruments.map(
                (instrument) => instrumentsLookup[instrument.isin] ?? instrument,
              ),
            },
          }),
          {},
        ),
      };
    }),
    on(
      discoverActions.loadMultiOrderStrategies,
      (state): DiscoverState => ({
        ...state,
        multiOrderStrategiesLoading: true,
      }),
    ),
    on(
      discoverApiActions.loadMultiOrderStrategiesSuccess,
      (state, { strategies }): DiscoverState => ({
        ...state,
        multiOrderStrategies: strategies,
        multiOrderStrategiesLoading: false,
      }),
    ),
    on(
      discoverApiActions.loadMultiOrderStrategiesFailure,
      (state): DiscoverState => ({
        ...state,
        multiOrderStrategiesLoading: false,
      }),
    ),
    on(
      discoverActions.clearMultiOrderStrategy,
      (state): DiscoverState => ({
        ...state,
        multiOrderStrategy: null,
        multiOrderStrategyAssetsLoaded: false,
      }),
    ),
    on(
      discoverApiActions.loadMultiOrderStrategySuccess,
      (state, { strategy }): DiscoverState => ({
        ...state,
        multiOrderStrategy: strategy,
        multiOrderStrategyAssetsLoaded: false,
      }),
    ),
    on(
      discoverApiActions.loadMultiOrderStrategyAssetsSuccess,
      (state, { assets }): DiscoverState => ({
        ...state,
        multiOrderStrategy: {
          ...state.multiOrderStrategy,
          assets,
        },
        multiOrderStrategyAssetsLoaded: true,
      }),
    ),
    on(discoverActions.updateMultiOrderStrategyAssetQuotes, (state, { quotes }): DiscoverState => {
      return {
        ...state,
        multiOrderStrategy: {
          ...state.multiOrderStrategy,
          assets: state.multiOrderStrategy?.assets?.map((asset) => {
            const quote = quotes[asset.isin];
            return quote ? { ...asset, quote } : asset;
          }),
        },
      };
    }),
    on(
      discoverActions.clearMultiOrderStrategies,
      (state): DiscoverState => ({
        ...state,
        multiOrderStrategies: null,
      }),
    ),
  ),
});
